import { Options } from '../options.interface';
import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { buildUrl } from '@wizbii-utils/angular/core';
import { Address, AutocompleteItem, City, LangEnum } from '@wizbii/utils/models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type QueryParamsType = 'city' | 'country';
export type AddressProvider = 'algolia' | 'google';

export interface GetByQueryParams {
  query?: string;
  type?: QueryParamsType;
  countries?: string[]; // ex 'FR', 'IT', 'GB'
  language?: LangEnum;
  preferredCountry?: string;
}

export interface ZipcodeQueryParams {
  zipCode: string;
  language: string;
  preferredCountry: string;
  countries: string[];
}

export interface PositionParamsType {
  lat: number;
  long: number;
}

export interface GetReverseQueryParams {
  position?: PositionParamsType;
}

export interface SearchAddressParams {
  query: string;
  countries?: string[];
  language?: LangEnum;
  session?: string;
}

export interface PlaceApiConfig {
  baseUrl: string;
}

export const PLACE_API_CONFIG = new InjectionToken<PlaceApiConfig>('PLACE_API_CONFIG');

@Injectable()
export class CityWebservice {
  readonly #http = inject(HttpClient);
  readonly #placeApiConfig = inject(PLACE_API_CONFIG);

  readonly #baseUrl = this.#placeApiConfig.baseUrl;

  /**
   * Get a city
   * @param {string} id Id of the city
   * @param {Object} [options] Angular Options for the request
   */
  get(id: string, options?: Options): Observable<City> {
    return this.#http
      .get(buildUrl(`${this.#baseUrl}/v2/location/?:id`, { id }), options)
      .pipe(map((item) => new City(item)));
  }

  /**
   * Get places objects with parameters
   * @param {Object} params  The query, the countries to restrict, the type
   * @param {Object} [options] Angular Options for the request
   */
  getBy(
    { query, countries, type, language, preferredCountry }: GetByQueryParams,
    options?: Options
  ): Observable<City[]> {
    const url = buildUrl(`${this.#baseUrl}/v2/location`, {
      ...(query ? { name: query } : {}),
      ...(type ? { type } : {}),
      ...(countries && countries.length ? { countries: countries.join() } : {}),
      ...(language ? { language } : {}),
      ...(preferredCountry ? { preferredCountry } : { preferredCountry: 'fr' }),
    });

    return this.#http
      .get<Record<string, unknown>[]>(url, options)
      .pipe(map((items) => (items || []).map((item) => new City(item))));
  }

  /**
   * Get places objects with zipCode
   * @param {Object} params  zipCode, countries to restrict
   * @param {Object} [options] Angular Options for the request
   */
  getByZipcode(
    { zipCode, countries, language, preferredCountry }: ZipcodeQueryParams,
    options?: Options
  ): Observable<City[]> {
    const url = buildUrl(`${this.#baseUrl}/v2/location/zip-code`, {
      ...(zipCode ? { zipCode } : {}),
      ...(countries && countries.length ? { countries: countries.join() } : {}),
      ...(language ? { language } : {}),
      ...(preferredCountry ? { preferredCountry } : { preferredCountry: 'fr' }),
    });

    return this.#http
      .get<Record<string, unknown>[]>(url, options)
      .pipe(map((items) => (items || []).map((item) => new City(item))));
  }

  /**
   * Get autocomplete items for addresses
   * @param {Object} params The query and filters
   * @param {Object} [options] Angular Options for the request
   */
  searchAddressBy(
    { query, language, countries, session }: SearchAddressParams,
    options?: Options
  ): Observable<AutocompleteItem[]> {
    const url = buildUrl(`${this.#baseUrl}/address`, {
      name: query,
      ...(countries && countries.length ? { countries: countries.join() } : {}),
      ...(language ? { language } : {}),
      ...(session ? { session } : {}),
    });

    return this.#http.get<AutocompleteItem[]>(url, options);
  }

  /**
   * Get the full object of an address with its ID
   * @param {string} id
   * @param {string} session The session used in the autocomplete search. Do not reuse it.
   * @param {Object} [options] Angular Options for the request
   */
  getAddressDetails(id: string, session?: string, options?: Options): Observable<City> {
    return this.#http
      .get(
        buildUrl(`${this.#baseUrl}/address/:id`, {
          id,
          ...(session ? { session } : {}),
        }),
        options
      )
      .pipe(map((item) => new City(item)));
  }

  /**
   * @deprecated prefer searchAddressBy & getAddressDetails as a 2-step workflow
   *
   * Get address objects with parameters
   * @param {Object} params  The query, the countries to restrict, the type
   * @param {Object} [options] Angular Options for the request
   */
  getAddressBy({ query, countries, type, language }: GetByQueryParams, options?: Options): Observable<Address[]> {
    const url = buildUrl(`${this.#baseUrl}/v2/address`, {
      ...(query ? { name: query } : {}),
      ...(type ? { type } : {}),
      ...(countries && countries.length ? { countries: countries.join() } : {}),
      ...(language ? { language } : {}),
    });

    return this.#http
      .get<Record<string, unknown>[]>(url, options)
      .pipe(map((items) => (items || []).map((item) => new Address(item))));
  }

  /**
   * Get places objects with parameters
   * @param {Object} params  The query, the countries to restrict, the type
   * @param {Object} [options] Angular Options for the request
   */
  getReverse({ position }: GetReverseQueryParams, options?: Options): Observable<City | undefined> {
    const url = buildUrl(`${this.#baseUrl}/v2/location/reverse`, {
      ...(position ? { lat: position.lat.toString(10), long: position.long.toString(10) } : {}),
    });

    return this.#http.get<Record<string, unknown>[]>(url, options).pipe(
      map((cities) => (cities || []).map((city) => new City(city))),
      map((cities) => cities.find((item) => item.city !== null))
    );
  }

  getPreferredAddressProvider(): Observable<AddressProvider> {
    return this.#http
      .get<{ provider: AddressProvider }>(buildUrl(`${this.#baseUrl}/address-provider`))
      .pipe(map((response) => response.provider));
  }
}
